import { useEffect, useState } from 'react';
import { 
  Save, Palette, Menu, FileText, Megaphone, Settings,
  RefreshCw, Eye, Plus, Trash2, GripVertical, ChevronRight,
  Edit, Copy, History, Check, X, ExternalLink, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/components/ThemeProvider';

export default function SiteBuilder() {
  const [activeTab, setActiveTab] = useState('theme');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { refreshTheme, applyTheme } = useTheme();
  
  // Site Builder State
  const [theme, setTheme] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [pages, setPages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  
  // Dialog States
  const [showNavDialog, setShowNavDialog] = useState(false);
  const [showPageDialog, setShowPageDialog] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [editingNav, setEditingNav] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [themeRes, navRes, pagesRes, annRes, storeRes] = await Promise.all([
        api.get('/site-builder/theme'),
        api.get('/site-builder/navigation'),
        api.get('/site-builder/pages'),
        api.get('/site-builder/announcements'),
        api.get('/site-builder/store-settings')
      ]);
      
      setTheme(themeRes.data);
      setNavigation(navRes.data.menus || []);
      setPages(pagesRes.data.pages || []);
      setAnnouncements(annRes.data.announcements || []);
      setStoreSettings(storeRes.data);
    } catch (error) {
      toast.error('Failed to load site builder data');
    } finally {
      setLoading(false);
    }
  };

  // Theme Functions
  const saveTheme = async () => {
    setSaving(true);
    try {
      await api.put('/site-builder/theme', { tokens: theme.tokens });
      
      // Also sync to legacy site settings
      const legacyTheme = {
        primary: theme.tokens.color_primary,
        secondary: theme.tokens.color_secondary,
        accent: theme.tokens.color_accent,
        background: theme.tokens.color_background
      };
      
      try {
        await api.put('/admin/site-settings', { theme: legacyTheme });
      } catch (e) {
        console.log('Legacy settings sync skipped');
      }
      
      // Apply theme immediately to the page
      applyTheme(theme.tokens);
      // Refresh the global theme context
      refreshTheme();
      toast.success('Theme saved and applied successfully!');
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  // Apply theme preview as user edits
  const updateToken = (key, value) => {
    const newTokens = { ...theme.tokens, [key]: value };
    setTheme(prev => ({
      ...prev,
      tokens: newTokens
    }));
    // Live preview - apply changes immediately
    applyTheme(newTokens);
  };

  // Navigation Functions
  const saveNavMenu = async () => {
    try {
      if (editingNav?.menu_id) {
        await api.put(`/site-builder/navigation/${editingNav.menu_id}`, editingNav);
        toast.success('Menu updated');
      } else {
        await api.post('/site-builder/navigation', editingNav);
        toast.success('Menu created');
      }
      setShowNavDialog(false);
      setEditingNav(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save menu');
    }
  };

  const deleteNavMenu = async (menuId) => {
    if (!confirm('Delete this navigation menu?')) return;
    try {
      await api.delete(`/site-builder/navigation/${menuId}`);
      toast.success('Menu deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete menu');
    }
  };

  const addNavItem = () => {
    setEditingNav(prev => ({
      ...prev,
      items: [...(prev.items || []), { 
        item_id: `nav_new_${Date.now()}`,
        title: 'New Link', 
        url: '/', 
        type: 'link',
        position: (prev.items?.length || 0)
      }]
    }));
  };

  const updateNavItem = (index, field, value) => {
    setEditingNav(prev => {
      const items = [...(prev.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const removeNavItem = (index) => {
    setEditingNav(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Page Functions
  const savePage = async () => {
    try {
      if (editingPage?.page_id) {
        await api.put(`/site-builder/pages/${editingPage.page_id}`, editingPage);
        toast.success('Page updated');
      } else {
        await api.post('/site-builder/pages', editingPage);
        toast.success('Page created');
      }
      setShowPageDialog(false);
      setEditingPage(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    }
  };

  const deletePage = async (pageId) => {
    if (!confirm('Delete this page?')) return;
    try {
      await api.delete(`/site-builder/pages/${pageId}`);
      toast.success('Page deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete page');
    }
  };

  const publishPage = async (pageId) => {
    try {
      await api.put(`/site-builder/pages/${pageId}`, { status: 'published' });
      toast.success('Page published');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to publish page');
    }
  };

  // Announcement Functions
  const saveAnnouncement = async () => {
    try {
      if (editingAnnouncement?.announcement_id) {
        await api.put(`/site-builder/announcements/${editingAnnouncement.announcement_id}`, editingAnnouncement);
        toast.success('Announcement updated');
      } else {
        await api.post('/site-builder/announcements', editingAnnouncement);
        toast.success('Announcement created');
      }
      setShowAnnouncementDialog(false);
      setEditingAnnouncement(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/site-builder/announcements/${announcementId}`);
      toast.success('Announcement deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // Store Settings Functions
  const saveStoreSettings = async () => {
    setSaving(true);
    try {
      await api.put('/site-builder/store-settings', storeSettings);
      toast.success('Store settings saved');
    } catch (error) {
      toast.error('Failed to save store settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" data-testid="site-builder-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-stone-900">Site Builder</h1>
              <p className="text-sm text-stone-500">Customize your entire website</p>
            </div>
            <div className="flex items-center gap-3">
              <a href="/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </a>
              <Button variant="outline" size="sm" onClick={fetchAllData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border grid grid-cols-5 h-auto p-1">
            <TabsTrigger value="theme" className="flex items-center gap-2 py-2">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Theme</span>
            </TabsTrigger>
            <TabsTrigger value="navigation" className="flex items-center gap-2 py-2">
              <Menu className="h-4 w-4" />
              <span className="hidden sm:inline">Navigation</span>
            </TabsTrigger>
            <TabsTrigger value="pages" className="flex items-center gap-2 py-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Pages</span>
            </TabsTrigger>
            <TabsTrigger value="announcements" className="flex items-center gap-2 py-2">
              <Megaphone className="h-4 w-4" />
              <span className="hidden sm:inline">Banners</span>
            </TabsTrigger>
            <TabsTrigger value="store" className="flex items-center gap-2 py-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Store</span>
            </TabsTrigger>
          </TabsList>

          {/* Theme Tab */}
          <TabsContent value="theme">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Theme Settings</CardTitle>
                    <CardDescription>Customize your website&apos;s colors and typography</CardDescription>
                  </div>
                  <Button onClick={saveTheme} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Theme
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible defaultValue="colors" className="space-y-4">
                  <AccordionItem value="colors" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <span className="font-medium">Colors</span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        {[
                          { key: 'color_primary', label: 'Primary', desc: 'Buttons, headers' },
                          { key: 'color_secondary', label: 'Secondary', desc: 'Borders, subtle elements' },
                          { key: 'color_accent', label: 'Accent', desc: 'Muted text, highlights' },
                          { key: 'color_background', label: 'Background', desc: 'Page background' },
                          { key: 'color_text', label: 'Text', desc: 'Main text color' },
                          { key: 'color_text_muted', label: 'Muted Text', desc: 'Secondary text' },
                        ].map(({ key, label, desc }) => (
                          <div key={key} className="space-y-2">
                            <Label>{label}</Label>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-10 h-10 rounded border-2 border-stone-200 overflow-hidden"
                                style={{ backgroundColor: theme?.tokens?.[key] || '#000' }}
                              >
                                <input
                                  type="color"
                                  value={theme?.tokens?.[key] || '#000000'}
                                  onChange={(e) => updateToken(key, e.target.value)}
                                  className="w-full h-full opacity-0 cursor-pointer"
                                />
                              </div>
                              <Input
                                value={theme?.tokens?.[key] || ''}
                                onChange={(e) => updateToken(key, e.target.value)}
                                className="flex-1 font-mono text-sm"
                              />
                            </div>
                            <p className="text-xs text-stone-500">{desc}</p>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="typography" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <span className="font-medium">Typography</span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        {[
                          { key: 'font_primary', label: 'Primary Font', placeholder: "'Inter', sans-serif" },
                          { key: 'font_heading', label: 'Heading Font', placeholder: "'Inter', sans-serif" },
                          { key: 'font_accent', label: 'Accent Font', placeholder: "'Georgia', serif" },
                          { key: 'font_size_base', label: 'Base Size', placeholder: '16px' },
                          { key: 'font_size_lg', label: 'Large Size', placeholder: '18px' },
                          { key: 'font_size_xl', label: 'XL Size', placeholder: '24px' },
                        ].map(({ key, label, placeholder }) => (
                          <div key={key} className="space-y-2">
                            <Label>{label}</Label>
                            <Input
                              value={theme?.tokens?.[key] || ''}
                              onChange={(e) => updateToken(key, e.target.value)}
                              placeholder={placeholder}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="spacing" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <span className="font-medium">Spacing & Borders</span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                          { key: 'radius_sm', label: 'Border Radius SM', placeholder: '4px' },
                          { key: 'radius_md', label: 'Border Radius MD', placeholder: '8px' },
                          { key: 'radius_lg', label: 'Border Radius LG', placeholder: '16px' },
                          { key: 'radius_full', label: 'Border Radius Full', placeholder: '9999px' },
                        ].map(({ key, label, placeholder }) => (
                          <div key={key} className="space-y-2">
                            <Label>{label}</Label>
                            <Input
                              value={theme?.tokens?.[key] || ''}
                              onChange={(e) => updateToken(key, e.target.value)}
                              placeholder={placeholder}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* Theme Preview */}
                <div className="mt-8 pt-6 border-t">
                  <Label className="mb-4 block">Preview</Label>
                  <div 
                    className="rounded-lg p-6 border"
                    style={{ backgroundColor: theme?.tokens?.color_background }}
                  >
                    <h3 
                      className="text-xl font-semibold mb-2"
                      style={{ color: theme?.tokens?.color_text, fontFamily: theme?.tokens?.font_heading }}
                    >
                      Preview Heading
                    </h3>
                    <p style={{ color: theme?.tokens?.color_text, fontFamily: theme?.tokens?.font_primary }}>
                      This is how your main text will look on the website.
                    </p>
                    <p style={{ color: theme?.tokens?.color_text_muted }} className="text-sm mt-2">
                      This is muted/secondary text.
                    </p>
                    <div className="flex items-center gap-3 mt-4">
                      <button 
                        className="px-4 py-2 rounded text-white text-sm"
                        style={{ 
                          backgroundColor: theme?.tokens?.color_primary,
                          borderRadius: theme?.tokens?.radius_md
                        }}
                      >
                        Primary Button
                      </button>
                      <button 
                        className="px-4 py-2 text-sm border"
                        style={{ 
                          borderColor: theme?.tokens?.color_secondary,
                          color: theme?.tokens?.color_text,
                          borderRadius: theme?.tokens?.radius_md
                        }}
                      >
                        Secondary Button
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Navigation Tab */}
          <TabsContent value="navigation">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Navigation Menus</CardTitle>
                    <CardDescription>Manage your site&apos;s navigation structure</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingNav({ name: '', location: 'header', items: [] });
                    setShowNavDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Menu
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {navigation.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <Menu className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                    <p>No navigation menus created yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {navigation.map((menu) => (
                      <div key={menu.menu_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-medium">{menu.name}</h3>
                            <Badge variant="secondary" className="text-xs">{menu.location}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setEditingNav(menu);
                                setShowNavDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => deleteNavMenu(menu.menu_id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {menu.items?.map((item, idx) => (
                            <span key={idx} className="text-sm bg-stone-100 px-2 py-1 rounded">
                              {item.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>CMS Pages</CardTitle>
                    <CardDescription>Create and manage content pages</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingPage({ title: '', slug: '', template: 'default', status: 'draft', blocks: [], seo: {} });
                    setShowPageDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Page
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {pages.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                    <p>No pages created yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pages.map((page) => (
                      <div key={page.page_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-stone-50">
                        <div className="flex items-center gap-4">
                          <FileText className="h-8 w-8 text-stone-400" />
                          <div>
                            <h3 className="font-medium">{page.title}</h3>
                            <p className="text-sm text-stone-500">/{page.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            page.status === 'published' ? 'bg-green-100 text-green-800' :
                            page.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {page.status}
                          </Badge>
                          <div className="flex items-center gap-1">
                            {page.status !== 'published' && (
                              <Button variant="outline" size="sm" onClick={() => publishPage(page.page_id)}>
                                <Globe className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setEditingPage(page);
                                setShowPageDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => deletePage(page.page_id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Announcements & Banners</CardTitle>
                    <CardDescription>Create promotional banners for your site</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingAnnouncement({ 
                      content: '', 
                      background_color: '#1c1917', 
                      text_color: '#ffffff',
                      status: 'draft',
                      position: 'top',
                      dismissible: true
                    });
                    setShowAnnouncementDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Banner
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {announcements.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <Megaphone className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                    <p>No announcements created yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((ann) => (
                      <div key={ann.announcement_id} className="border rounded-lg overflow-hidden">
                        <div 
                          className="p-3 text-center text-sm"
                          style={{ backgroundColor: ann.background_color, color: ann.text_color }}
                        >
                          {ann.content}
                          {ann.link_text && <span className="underline ml-2">{ann.link_text}</span>}
                        </div>
                        <div className="flex items-center justify-between p-3 bg-stone-50">
                          <div className="flex items-center gap-2">
                            <Badge className={
                              ann.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }>
                              {ann.status}
                            </Badge>
                            <span className="text-xs text-stone-500">{ann.position}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingAnnouncement(ann);
                                setShowAnnouncementDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteAnnouncement(ann.announcement_id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Store Settings Tab */}
          <TabsContent value="store">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Store Settings</CardTitle>
                    <CardDescription>Configure your store&apos;s basic information</CardDescription>
                  </div>
                  <Button onClick={saveStoreSettings} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Settings
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Store Name</Label>
                    <Input
                      value={storeSettings?.store_name || ''}
                      onChange={(e) => setStoreSettings({...storeSettings, store_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Store Email</Label>
                    <Input
                      type="email"
                      value={storeSettings?.store_email || ''}
                      onChange={(e) => setStoreSettings({...storeSettings, store_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Support Email</Label>
                    <Input
                      type="email"
                      value={storeSettings?.support_email || ''}
                      onChange={(e) => setStoreSettings({...storeSettings, support_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={storeSettings?.phone || ''}
                      onChange={(e) => setStoreSettings({...storeSettings, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Currency & Tax</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select 
                        value={storeSettings?.currency || 'SGD'}
                        onValueChange={(v) => setStoreSettings({...storeSettings, currency: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                          <SelectItem value="USD">USD - US Dollar</SelectItem>
                          <SelectItem value="EUR">EUR - Euro</SelectItem>
                          <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Currency Symbol</Label>
                      <Input
                        value={storeSettings?.currency_symbol || ''}
                        onChange={(e) => setStoreSettings({...storeSettings, currency_symbol: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tax Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={(storeSettings?.tax_rate || 0) * 100}
                        onChange={(e) => setStoreSettings({...storeSettings, tax_rate: parseFloat(e.target.value) / 100})}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Shipping</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Free Shipping Threshold</Label>
                      <Input
                        type="number"
                        value={storeSettings?.free_shipping_threshold || 0}
                        onChange={(e) => setStoreSettings({...storeSettings, free_shipping_threshold: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Default Shipping Rate</Label>
                      <Input
                        type="number"
                        value={storeSettings?.default_shipping_rate || 0}
                        onChange={(e) => setStoreSettings({...storeSettings, default_shipping_rate: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Checkout Options</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Guest Checkout</Label>
                        <p className="text-sm text-stone-500">Allow customers to checkout without an account</p>
                      </div>
                      <Switch
                        checked={storeSettings?.guest_checkout_enabled ?? true}
                        onCheckedChange={(v) => setStoreSettings({...storeSettings, guest_checkout_enabled: v})}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Require Phone Number</Label>
                        <p className="text-sm text-stone-500">Require phone number at checkout</p>
                      </div>
                      <Switch
                        checked={storeSettings?.require_phone ?? false}
                        onCheckedChange={(v) => setStoreSettings({...storeSettings, require_phone: v})}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Order Notes</Label>
                        <p className="text-sm text-stone-500">Allow customers to add notes to orders</p>
                      </div>
                      <Switch
                        checked={storeSettings?.order_notes_enabled ?? true}
                        onCheckedChange={(v) => setStoreSettings({...storeSettings, order_notes_enabled: v})}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Social Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Instagram URL</Label>
                      <Input
                        value={storeSettings?.social_links?.instagram || ''}
                        onChange={(e) => setStoreSettings({
                          ...storeSettings, 
                          social_links: {...(storeSettings?.social_links || {}), instagram: e.target.value}
                        })}
                        placeholder="https://instagram.com/yourstore or @yourstore"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email</Label>
                      <Input
                        type="email"
                        value={storeSettings?.social_links?.email || storeSettings?.store_email || ''}
                        onChange={(e) => setStoreSettings({
                          ...storeSettings, 
                          social_links: {...(storeSettings?.social_links || {}), email: e.target.value}
                        })}
                        placeholder="contact@yourstore.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">FAQ</h3>
                  <div className="space-y-4">
                    {(storeSettings?.faq || []).map((item, index) => (
                      <div key={index} className="p-4 border rounded-lg space-y-3">
                        <div className="flex justify-between items-start">
                          <span className="text-sm text-stone-500">Question {index + 1}</span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              const newFaq = [...(storeSettings?.faq || [])];
                              newFaq.splice(index, 1);
                              setStoreSettings({...storeSettings, faq: newFaq});
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                        <Input
                          value={item.question}
                          onChange={(e) => {
                            const newFaq = [...(storeSettings?.faq || [])];
                            newFaq[index].question = e.target.value;
                            setStoreSettings({...storeSettings, faq: newFaq});
                          }}
                          placeholder="Enter question"
                        />
                        <Textarea
                          value={item.answer}
                          onChange={(e) => {
                            const newFaq = [...(storeSettings?.faq || [])];
                            newFaq[index].answer = e.target.value;
                            setStoreSettings({...storeSettings, faq: newFaq});
                          }}
                          placeholder="Enter answer"
                          rows={2}
                        />
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      onClick={() => {
                        const newFaq = [...(storeSettings?.faq || []), { question: '', answer: '' }];
                        setStoreSettings({...storeSettings, faq: newFaq});
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add FAQ Item
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Shipping & Returns</h3>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Shipping Information</Label>
                      <Textarea
                        value={storeSettings?.shipping_returns?.shipping_info || ''}
                        onChange={(e) => setStoreSettings({
                          ...storeSettings, 
                          shipping_returns: {...(storeSettings?.shipping_returns || {}), shipping_info: e.target.value}
                        })}
                        placeholder="Enter shipping information (e.g., delivery times, shipping methods, costs)"
                        rows={5}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Returns Policy</Label>
                      <Textarea
                        value={storeSettings?.shipping_returns?.returns_info || ''}
                        onChange={(e) => setStoreSettings({
                          ...storeSettings, 
                          shipping_returns: {...(storeSettings?.shipping_returns || {}), returns_info: e.target.value}
                        })}
                        placeholder="Enter returns policy (e.g., return window, conditions, process)"
                        rows={5}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Navigation Dialog */}
      <Dialog open={showNavDialog} onOpenChange={setShowNavDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingNav?.menu_id ? 'Edit Menu' : 'Create Menu'}</DialogTitle>
            <DialogDescription>Configure your navigation menu</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Menu Name</Label>
                <Input
                  value={editingNav?.name || ''}
                  onChange={(e) => setEditingNav({...editingNav, name: e.target.value})}
                  placeholder="Header Navigation"
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Select 
                  value={editingNav?.location || 'header'}
                  onValueChange={(v) => setEditingNav({...editingNav, location: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="header">Header</SelectItem>
                    <SelectItem value="footer">Footer</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Menu Items</Label>
                <Button variant="outline" size="sm" onClick={addNavItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {editingNav?.items?.map((item, idx) => (
                  <div key={item.item_id || idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                    <GripVertical className="h-4 w-4 text-stone-400" />
                    <Input
                      value={item.title}
                      onChange={(e) => updateNavItem(idx, 'title', e.target.value)}
                      placeholder="Link Title"
                      className="flex-1"
                    />
                    <Input
                      value={item.url}
                      onChange={(e) => updateNavItem(idx, 'url', e.target.value)}
                      placeholder="/path"
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeNavItem(idx)}>
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNavDialog(false)}>Cancel</Button>
            <Button onClick={saveNavMenu}>Save Menu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Dialog */}
      <Dialog open={showPageDialog} onOpenChange={setShowPageDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingPage?.page_id ? 'Edit Page' : 'Create Page'}</DialogTitle>
            <DialogDescription>Configure your content page</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Page Title</Label>
              <Input
                value={editingPage?.title || ''}
                onChange={(e) => setEditingPage({...editingPage, title: e.target.value})}
                placeholder="About Us"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>URL Slug</Label>
                <div className="flex items-center gap-1">
                  <span className="text-stone-500">/</span>
                  <Input
                    value={editingPage?.slug || ''}
                    onChange={(e) => setEditingPage({...editingPage, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                    placeholder="about-us"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Template</Label>
                <Select 
                  value={editingPage?.template || 'default'}
                  onValueChange={(v) => setEditingPage({...editingPage, template: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="contact">Contact</SelectItem>
                    <SelectItem value="landing">Landing</SelectItem>
                    <SelectItem value="blog">Blog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={editingPage?.status || 'draft'}
                onValueChange={(v) => setEditingPage({...editingPage, status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t pt-4">
              <Label className="mb-2 block">SEO Settings</Label>
              <div className="space-y-3">
                <Input
                  value={editingPage?.seo?.title || ''}
                  onChange={(e) => setEditingPage({...editingPage, seo: {...editingPage.seo, title: e.target.value}})}
                  placeholder="SEO Title"
                />
                <Textarea
                  value={editingPage?.seo?.description || ''}
                  onChange={(e) => setEditingPage({...editingPage, seo: {...editingPage.seo, description: e.target.value}})}
                  placeholder="SEO Description"
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPageDialog(false)}>Cancel</Button>
            <Button onClick={savePage}>Save Page</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAnnouncement?.announcement_id ? 'Edit Banner' : 'Create Banner'}</DialogTitle>
            <DialogDescription>Configure your announcement banner</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Banner Text</Label>
              <Textarea
                value={editingAnnouncement?.content || ''}
                onChange={(e) => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})}
                placeholder="Free shipping on orders over $100!"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link URL (optional)</Label>
                <Input
                  value={editingAnnouncement?.link_url || ''}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, link_url: e.target.value})}
                  placeholder="/shop"
                />
              </div>
              <div className="space-y-2">
                <Label>Link Text</Label>
                <Input
                  value={editingAnnouncement?.link_text || ''}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, link_text: e.target.value})}
                  placeholder="Shop Now"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-10 h-10 rounded border overflow-hidden"
                    style={{ backgroundColor: editingAnnouncement?.background_color || '#1c1917' }}
                  >
                    <input
                      type="color"
                      value={editingAnnouncement?.background_color || '#1c1917'}
                      onChange={(e) => setEditingAnnouncement({...editingAnnouncement, background_color: e.target.value})}
                      className="w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                  <Input
                    value={editingAnnouncement?.background_color || ''}
                    onChange={(e) => setEditingAnnouncement({...editingAnnouncement, background_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-10 h-10 rounded border overflow-hidden"
                    style={{ backgroundColor: editingAnnouncement?.text_color || '#ffffff' }}
                  >
                    <input
                      type="color"
                      value={editingAnnouncement?.text_color || '#ffffff'}
                      onChange={(e) => setEditingAnnouncement({...editingAnnouncement, text_color: e.target.value})}
                      className="w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                  <Input
                    value={editingAnnouncement?.text_color || ''}
                    onChange={(e) => setEditingAnnouncement({...editingAnnouncement, text_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Position</Label>
                <Select 
                  value={editingAnnouncement?.position || 'top'}
                  onValueChange={(v) => setEditingAnnouncement({...editingAnnouncement, position: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Top of Page</SelectItem>
                    <SelectItem value="bottom">Bottom of Page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={editingAnnouncement?.status || 'draft'}
                  onValueChange={(v) => setEditingAnnouncement({...editingAnnouncement, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Preview */}
            <div className="border-t pt-4">
              <Label className="mb-2 block">Preview</Label>
              <div 
                className="p-3 text-center text-sm rounded"
                style={{ 
                  backgroundColor: editingAnnouncement?.background_color || '#1c1917', 
                  color: editingAnnouncement?.text_color || '#ffffff' 
                }}
              >
                {editingAnnouncement?.content || 'Your banner text here'}
                {editingAnnouncement?.link_text && (
                  <span className="underline ml-2">{editingAnnouncement.link_text}</span>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>Cancel</Button>
            <Button onClick={saveAnnouncement}>Save Banner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
